import { BoxProps } from '@chakra-ui/react';
import {
  AccountDocumentDetailResponse,
  AccountDocumentStatusEnum,
} from '@monetize/types/app';
import { useCallback, useMemo, useState } from 'react';
import {
  useGetAccountDocumentFile,
  useUpdateAccountDocument,
} from '../../../../api/accountsService';
import { handleApiErrorToast } from '../../../../api/axios';
import {
  MBox,
  MButton,
  MCenterModal,
  MFlex,
  MStack,
  MTab,
  MTabList,
  MTabPanel,
  MTabPanels,
  MTabs,
} from '../../../../components/Monetize';
import { MFileDragDropUpload } from '../../../../components/Monetize/MFileDragDropUpload';
import { PreviewPdf } from '../../../../components/Monetize/PreviewFile/PreviewPdf';
import { base64ToArrayBuffer } from '../../../../utils';

interface AccountDocumentPDFProps extends BoxProps {
  accountDocument: AccountDocumentDetailResponse;
  isReadOnly?: boolean;
  refetchAccountDocument: () => void;
}

export const AccountDocumentPDF = ({
  accountDocument,
  isReadOnly,
  refetchAccountDocument,
  ...rest
}: AccountDocumentPDFProps) => {
  const {
    data: originalDocument,
    isLoading: isLoadingOriginalDocument,
    refetch: refetchOriginalDocument,
  } = useGetAccountDocumentFile(
    accountDocument.accountId,
    accountDocument.id,
    accountDocument.storageObjectId,
  );

  const [isModalOpen, toggleModalOpen] = useState(false);
  const [attachmentFile, setAttachmentFile] = useState<File | null>(null);

  const onDropAccepted = useCallback(async (acceptedFiles: File) => {
    setAttachmentFile(acceptedFiles);
  }, []);

  const {
    mutateAsync: doUpdateAccountDocument,
    isPending: isUpdatingAccountDocument,
  } = useUpdateAccountDocument({
    onSuccess: async () => {
      await refetchAccountDocument();
      setAttachmentFile(null);
      toggleModalOpen(false);
    },
    onError: (error) => {
      handleApiErrorToast(error);
    },
  });

  const isSigned = accountDocument.status === AccountDocumentStatusEnum.SIGNED;
  const originalDocumentArrayBuffer = useMemo(() => {
    return originalDocument?.contentBase64Encoded
      ? base64ToArrayBuffer(originalDocument.contentBase64Encoded)
      : null;
  }, [originalDocument?.contentBase64Encoded]);

  const { data: signedDocument, isLoading: isLoadingSignedDocument } =
    useGetAccountDocumentFile(
      accountDocument.accountId,
      accountDocument.id,
      accountDocument.signedDocumentStorageObjectId!,
      {
        enabled: isSigned,
      },
    );

  const signedDocumentArrayBuffer = useMemo(() => {
    return signedDocument?.contentBase64Encoded
      ? base64ToArrayBuffer(signedDocument.contentBase64Encoded)
      : null;
  }, [signedDocument]);

  const onCloseModal = () => {
    setAttachmentFile(null);
    toggleModalOpen(false);
  };

  const handleUploadNewFile = () => {
    if (!attachmentFile) {
      return;
    }

    doUpdateAccountDocument({
      accountId: accountDocument.accountId,
      accountDocumentId: accountDocument.id,
      payload: {
        name: accountDocument.name,
        description: accountDocument.description,
        signingOrder: accountDocument.signingOrder,
      },
      file: attachmentFile,
    });
  };
  return (
    <MBox mt="5" w="100%">
      <MTabs>
        <MTabList>
          <MTab>Original Document</MTab>
          {isSigned && <MTab>Signed Document</MTab>}
        </MTabList>
        <MTabPanels>
          <MTabPanel>
            <MFlex justifyContent="flex-end">
              {!isReadOnly && (
                <MButton
                  minW={20}
                  size="sm"
                  isDisabled={isLoadingOriginalDocument}
                  variant={'secondary'}
                  onClick={() => toggleModalOpen(true)}
                  mb="1"
                >
                  Replace File
                </MButton>
              )}
            </MFlex>
            <PreviewPdf
              filename={accountDocument.storageObject.filename}
              currentPdfFile={originalDocumentArrayBuffer}
              isLoading={isLoadingOriginalDocument}
              key={accountDocument.storageObject.id}
              showDownloadButton
            />
          </MTabPanel>
          {isSigned && (
            <MTabPanel>
              <PreviewPdf
                filename={accountDocument.storageObject.filename}
                currentPdfFile={signedDocumentArrayBuffer}
                isLoading={isLoadingSignedDocument}
                showDownloadButton
              />
            </MTabPanel>
          )}
        </MTabPanels>
      </MTabs>

      <MCenterModal
        isOpen={isModalOpen}
        onClose={onCloseModal}
        modalTitle={'Replace Document File'}
        modalHeaderProps={{ pb: 1 }}
        closeOnEsc={false}
        closeOnOverlayClick={false}
      >
        <form>
          <MBox flex="1">
            <MFileDragDropUpload
              accept={['pdf']}
              label="Drag and Drop your file here or Browse"
              onFileUpload={onDropAccepted}
              maxFileSize={10}
              innerContainerProps={{ p: '5', bg: 'white' }}
              selectedFile={attachmentFile}
            />

            <MStack
              gap={4}
              direction="row"
              align="center"
              justify="right"
              flex={1}
              mt={4}
            >
              <MButton
                variant="cancel"
                onClick={() => toggleModalOpen(false)}
                minW="auto"
              >
                Cancel
              </MButton>
              <MButton
                variant="primary"
                isLoading={isUpdatingAccountDocument}
                onClick={handleUploadNewFile}
                minW="auto"
                isDisabled={!attachmentFile}
              >
                Save
              </MButton>
            </MStack>
          </MBox>
        </form>
      </MCenterModal>
    </MBox>
  );
};
